import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Tooltip,
  Container,
  Dialog,
  DialogContent,
  DialogContentText,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Person as PersonIcon,
  FitnessCenter as FitnessIcon,
  Restaurant as RestaurantIcon,
  LocalFireDepartment as FireIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '20px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
  border: 'none',
  marginBottom: theme.spacing(3),
}));

const IconBox = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
}));

const MacroCard = styled(Box)(({ theme, color }) => ({
  padding: theme.spacing(2),
  borderRadius: '16px',
  backgroundColor: color || '#f8fafc',
  textAlign: 'center',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.02)',
  }
}));

const InfoRow = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', py: 1, borderBottom: '1px solid #f1f5f9' }}>
    <Typography sx={{ mr: 1, fontSize: '1rem' }}>{icon}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight="600" color="#1e293b">
      {value || '-'}
    </Typography>
  </Box>
);

const Summary = ({ userData, handleInputChange, handleSubmit: onboardingHandleSubmit }) => {
  const [calculatedMacros, setCalculatedMacros] = useState(null);
  const [editableMacros, setEditableMacros] = useState(null);
  const [hasEdited, setHasEdited] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onboardingHandleSubmit();
      setIsSubmitting(false);
      setShowSuccessDialog(true);
      setTimeout(() => {
        setShowSuccessDialog(false);
        navigate('/dashboard');
      }, 1000);
    } catch (error) {
      console.error('Error al guardar el perfil:', error);
      setIsSubmitting(false);
    }
  };

  const calcularMacros = useCallback(() => {
    const { sex, age, heightCM, weight, activityLevel, goal } = userData;

    // 1. Calculate TMB using Mifflin-St Jeor equation
    const calcularTMB = () => {
      if (sex === 'Masculino') {
        return 10 * weight + 6.25 * heightCM - 5 * age + 5;
      } else {
        return 10 * weight + 6.25 * heightCM - 5 * age - 161;
      }
    };

    // 2. Activity factor multipliers
    const calcularFactorActividad = (nivel) => {
      const factores = {
        'Sedentario': 1.2,
        'Ligeramente activo': 1.375,
        'Moderadamente activo': 1.55,
        'Muy activo': 1.725,
        'Extremadamente activo': 1.9
      };
      return factores[nivel] || 1.2;
    };

    // 3. Caloric adjustment based on goal
    const calcularCaloriasDiarias = (tmb, factorActividad, objetivo) => {
      let tdee = tmb * factorActividad;
      switch (objetivo) {
        case 'Perder peso':
          return tdee * 0.80; // 20% deficit for effective fat loss
        case 'Ganar masa muscular':
          return tdee * 1.10; // 10% surplus to minimize fat gain
        case 'Recomposición':
          return tdee * 0.92; // 8% deficit - enough for fat loss, allows muscle gain
        case 'Mejorar salud general':
        case 'Mantener peso':
        default:
          return tdee;
      }
    };

    // 4. Calculate macros based on body weight (scientifically optimal)
    const calcularMacronutrientes = (calorias, objetivo, pesoKg) => {
      let proteinasGrKg, grasasGrKg;

      // Protein requirements based on goal (g per kg body weight)
      switch (objetivo) {
        case 'Recomposición':
          proteinasGrKg = 2.2; // Maximum for preserving/building muscle in deficit
          grasasGrKg = 0.9;    // Adequate for hormonal health
          break;
        case 'Perder peso':
          proteinasGrKg = 2.0; // High to preserve muscle during deficit
          grasasGrKg = 0.8;    // Minimum for hormonal health
          break;
        case 'Ganar masa muscular':
          proteinasGrKg = 1.8; // Optimal for muscle protein synthesis
          grasasGrKg = 1.0;    // Good for hormonal support
          break;
        case 'Mejorar salud general':
        case 'Mantener peso':
        default:
          proteinasGrKg = 1.4; // Adequate for maintenance
          grasasGrKg = 0.9;    // Standard healthy intake
          break;
      }

      // Calculate actual grams
      const proteinasGr = Math.round(pesoKg * proteinasGrKg);
      const grasasGr = Math.round(pesoKg * grasasGrKg);

      // Protein = 4 kcal/g, Fat = 9 kcal/g
      const caloriasProteinas = proteinasGr * 4;
      const caloriasGrasas = grasasGr * 9;

      // Carbs = remaining calories / 4 kcal per gram
      const caloriasRestantes = calorias - caloriasProteinas - caloriasGrasas;
      const carbohidratosGr = Math.max(0, Math.round(caloriasRestantes / 4));

      return {
        proteinas: proteinasGr,
        grasas: grasasGr,
        carbohidratos: carbohidratosGr,
      };
    };

    const tmb = calcularTMB();
    const factorActividad = calcularFactorActividad(activityLevel);
    const caloriasDiarias = calcularCaloriasDiarias(tmb, factorActividad, goal);
    const macros = calcularMacronutrientes(caloriasDiarias, goal, weight);

    return {
      tmb: Math.round(tmb),
      factorActividad,
      caloriasDiarias: Math.round(caloriasDiarias),
      ...macros,
    };
  }, [userData]);

  useEffect(() => {
    if (userData.sex && userData.age && userData.heightCM && userData.weight && userData.activityLevel && userData.goal) {
      const macros = calcularMacros();
      setCalculatedMacros(macros);
      setEditableMacros(macros);
      handleInputChange('macros', {
        kilocalories: macros.caloriasDiarias,
        carbs: macros.carbohidratos,
        protein: macros.proteinas,
        fat: macros.grasas
      });
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, []);

  const handleMacroChange = useCallback((campo, valor) => {
    const newValue = parseFloat(valor) || 0;
    setEditableMacros(prev => {
      const updated = { ...prev, [campo]: newValue };
      handleInputChange('macros', {
        kilocalories: updated.caloriasDiarias,
        carbs: updated.carbohidratos,
        protein: updated.proteinas,
        fat: updated.grasas
      });
      return updated;
    });
    setHasEdited(true);
  }, [handleInputChange]);

  const resetToOriginal = useCallback(() => {
    if (calculatedMacros) {
      setEditableMacros(calculatedMacros);
      handleInputChange('macros', {
        kilocalories: calculatedMacros.caloriasDiarias,
        carbs: calculatedMacros.carbohidratos,
        protein: calculatedMacros.proteinas,
        fat: calculatedMacros.grasas
      });
      setHasEdited(false);
    }
  }, [calculatedMacros, handleInputChange]);

  const getSkillLabel = (value) => {
    const labels = { 1: 'Principiante', 2: 'Básico', 3: 'Intermedio', 4: 'Avanzado', 5: 'Chef' };
    return labels[value] || 'Principiante';
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  if (!editableMacros) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">
          Cargando resultados... Por favor, asegúrate de haber completado todos los campos necesarios.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Personal Info Card */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconBox sx={{ bgcolor: '#e0e7ff' }}>
              <PersonIcon sx={{ color: '#667eea' }} />
            </IconBox>
            <Typography variant="h6" fontWeight="600" color="#1e293b">
              Información Personal
            </Typography>
          </Box>

          <InfoRow icon="👤" label="Nombre" value={userData.name} />
          <InfoRow icon="🎂" label="Edad" value={`${userData.age} años`} />
          <InfoRow icon="⚥" label="Género" value={userData.sex} />
          <InfoRow icon="📏" label="Altura" value={`${userData.heightCM} cm`} />
          <InfoRow icon="⚖️" label="Peso actual" value={`${userData.weight} kg`} />
          <InfoRow icon="👨‍👩‍👧" label="Comensales" value={userData.comensalesCasa} />
        </CardContent>
      </StyledCard>

      {/* Goals & Activity Card */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconBox sx={{ bgcolor: '#dcfce7' }}>
              <FitnessIcon sx={{ color: '#22c55e' }} />
            </IconBox>
            <Typography variant="h6" fontWeight="600" color="#1e293b">
              Objetivos y Actividad
            </Typography>
          </Box>

          <InfoRow icon="🎯" label="Objetivo" value={userData.goal} />
          <InfoRow icon="🏃" label="Nivel de actividad" value={userData.activityLevel} />
        </CardContent>
      </StyledCard>

      {/* Culinary Experience Card */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconBox sx={{ bgcolor: '#fef3c7' }}>
              <RestaurantIcon sx={{ color: '#f59e0b' }} />
            </IconBox>
            <Typography variant="h6" fontWeight="600" color="#1e293b">
              Experiencia Culinaria
            </Typography>
          </Box>

          <InfoRow icon="👨‍🍳" label="Habilidad en la cocina" value={getSkillLabel(userData.cookingSkill)} />
          <InfoRow icon="⏱️" label="Tiempo disponible" value={userData.cookingTime} />
          <InfoRow icon="✨" label="Explorar recetas nuevas" value={userData.newRecipesInterest ? 'Sí' : 'No'} />
        </CardContent>
      </StyledCard>

      {/* Nutritional Calculation Card */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconBox sx={{ bgcolor: '#fee2e2' }}>
              <FireIcon sx={{ color: '#ef4444' }} />
            </IconBox>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="600" color="#1e293b">
                Tu Plan Nutricional
              </Typography>
              <Typography variant="body2" color="text.secondary">
                TMB: {editableMacros.tmb} kcal/día • Puedes editar los valores
              </Typography>
            </Box>
            <Tooltip title="Calculado con la fórmula Mifflin-St Jeor">
              <InfoIcon sx={{ color: '#94a3b8', cursor: 'pointer' }} />
            </Tooltip>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Calories */}
            <Grid item xs={6} sm={3}>
              <MacroCard color="#fef3c7">
                <Typography variant="h6" sx={{ mb: 0.5 }}>🔥</Typography>
                <TextField
                  type="number"
                  value={editableMacros.caloriasDiarias}
                  onChange={(e) => handleMacroChange('caloriasDiarias', e.target.value)}
                  variant="standard"
                  inputProps={{ style: { textAlign: 'center', fontWeight: 700, fontSize: '1.5rem' } }}
                  sx={{ width: '100%', '& .MuiInput-underline:before': { borderBottom: 'none' } }}
                />
                <Typography variant="caption" color="text.secondary">
                  Calorías
                </Typography>
              </MacroCard>
            </Grid>

            {/* Carbs */}
            <Grid item xs={6} sm={3}>
              <MacroCard color="#dbeafe">
                <Typography variant="h6" sx={{ mb: 0.5 }}>🍞</Typography>
                <TextField
                  type="number"
                  value={editableMacros.carbohidratos}
                  onChange={(e) => handleMacroChange('carbohidratos', e.target.value)}
                  variant="standard"
                  inputProps={{ style: { textAlign: 'center', fontWeight: 700, fontSize: '1.5rem' } }}
                  InputProps={{ endAdornment: <InputAdornment position="end">g</InputAdornment> }}
                  sx={{ width: '100%', '& .MuiInput-underline:before': { borderBottom: 'none' } }}
                />
                <Typography variant="caption" color="text.secondary">
                  Carbohidratos
                </Typography>
              </MacroCard>
            </Grid>

            {/* Protein */}
            <Grid item xs={6} sm={3}>
              <MacroCard color="#dcfce7">
                <Typography variant="h6" sx={{ mb: 0.5 }}>🍗</Typography>
                <TextField
                  type="number"
                  value={editableMacros.proteinas}
                  onChange={(e) => handleMacroChange('proteinas', e.target.value)}
                  variant="standard"
                  inputProps={{ style: { textAlign: 'center', fontWeight: 700, fontSize: '1.5rem' } }}
                  InputProps={{ endAdornment: <InputAdornment position="end">g</InputAdornment> }}
                  sx={{ width: '100%', '& .MuiInput-underline:before': { borderBottom: 'none' } }}
                />
                <Typography variant="caption" color="text.secondary">
                  Proteínas
                </Typography>
              </MacroCard>
            </Grid>

            {/* Fat */}
            <Grid item xs={6} sm={3}>
              <MacroCard color="#fce7f3">
                <Typography variant="h6" sx={{ mb: 0.5 }}>🥑</Typography>
                <TextField
                  type="number"
                  value={editableMacros.grasas}
                  onChange={(e) => handleMacroChange('grasas', e.target.value)}
                  variant="standard"
                  inputProps={{ style: { textAlign: 'center', fontWeight: 700, fontSize: '1.5rem' } }}
                  InputProps={{ endAdornment: <InputAdornment position="end">g</InputAdornment> }}
                  sx={{ width: '100%', '& .MuiInput-underline:before': { borderBottom: 'none' } }}
                />
                <Typography variant="caption" color="text.secondary">
                  Grasas
                </Typography>
              </MacroCard>
            </Grid>
          </Grid>

          {hasEdited && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={resetToOriginal}
              sx={{
                borderRadius: '12px',
                borderColor: '#667eea',
                color: '#667eea',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#5a67d8',
                  bgcolor: '#e0e7ff',
                }
              }}
            >
              Restablecer valores calculados
            </Button>
          )}
        </CardContent>
      </StyledCard>

      {/* Submit Button */}
      <Button
        fullWidth
        variant="contained"
        onClick={handleSubmit}
        disabled={isSubmitting || showSuccessDialog}
        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
        sx={{
          borderRadius: '16px',
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '1.1rem',
          py: 2,
          bgcolor: '#667eea',
          boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
          '&:hover': {
            bgcolor: '#5a67d8',
          },
          '&.Mui-disabled': {
            bgcolor: '#e2e8f0',
          }
        }}
      >
        {isSubmitting ? 'Guardando...' : 'Finalizar y Guardar Perfil'}
      </Button>

      {/* Success Dialog */}
      <Dialog open={isSubmitting || showSuccessDialog} onClose={() => { }}>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          {isSubmitting ? (
            <CircularProgress sx={{ color: '#667eea' }} />
          ) : (
            <>
              <Typography variant="h4" sx={{ mb: 2 }}>✅</Typography>
              <Typography variant="h6" fontWeight="600">
                ¡Perfil guardado con éxito!
              </Typography>
            </>
          )}
          <DialogContentText sx={{ mt: 1 }}>
            {isSubmitting ? 'Guardando tu perfil...' : 'Redirigiendo al dashboard...'}
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Summary;
